import React, { useEffect, useState } from 'react'
import { Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import Service from '../Networking/NetworkingUtils'
import { getCookie } from '../Utils/CookieHelper'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

const token = getCookie('bb_metaverse_token')

const UserRequests = (props) => {
  const { enqueueSnackbar } = useSnackbar()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [selectedInvite, setSelectedInvite] = useState(null)
  const [accessList, setAccessList] = useState([])
  const [accessGroup, setAccessGroup] = useState([])
  const [access, setAccess] = useState([])
  const [approveValues, setApproveValues] = useState({
    start_date: null,
    end_date: null,
    project_access_list: null,
    access_group: [],
    access: [],
  })

  const getData = () => {
    Service.get(`/api/project/user/invite/get_users_access_request/?status=0&project=${props.project?.id}`, {
      headers: {
        Authorization: 'Token ' + token,
      },
    }).then((response) => {
      setRequests(response.data)
      setLoading(false)
    }).catch((error) => {
      console.log(error)
      setLoading(false)
      enqueueSnackbar('Something went wrong!', { variant: 'error' })
    })
  }

  const getAccessList = () => {
    return Service.get('/api/projects/access/list/get_project_access_list/', {
      headers: {
        Authorization: 'Token ' + token,
      },
    })
  }

  const getAccessGroup = () => {
    return Service.get(`/api/access/group/link/get_group_level_project_assess/?project_id=${props.project?.id}`, {
      headers: {
        Authorization: 'Token ' + token,
      },
    })
  }

  const getAccess = (id) => {
    return Service.get(`/api/access/management/get_access_data/?access_group_id=${id}`, {
      headers: {
        Authorization: 'Token ' + token,
      },
    })
  }

  const resetApproveDialog = () => {
    setApproveDialogOpen(false)
    setSelectedInvite(null)
    setApproveValues({
      start_date: null,
      end_date: null,
      project_access_list: null,
      access_group: [],
      access: [],
    })
    setAccess([])
  }

  const openApproveDialog = (invite) => {
    setSelectedInvite(invite)
    setApproveDialogOpen(true)
  }

  const approveRequest = () => {
    const { start_date, end_date, project_access_list, access_group, access: selectedAccess } = approveValues

    if (!start_date || !end_date || !project_access_list || access_group.length === 0 || selectedAccess.length === 0) {
      enqueueSnackbar('Please select all access parameters!', { variant: 'warning' })
      return
    }

    Service.post('/api/project/user/invite/approve_user_request/', {
      headers: {
        Authorization: 'Token ' + token,
      },
      data: {
        invite_id: selectedInvite,
        access_parameter: {
          start_date: start_date.format('YYYY-MM-DD'),
          end_date: end_date.format('YYYY-MM-DD'),
          project_access_list: project_access_list.id,
          access_group: access_group.map((group) => group.id),
          access: selectedAccess.map((item) => item.id),
        },
      },
    }).then(() => {
      enqueueSnackbar('User approved successfully!', { variant: 'success' })
      resetApproveDialog()
      getData()
    }).catch((error) => {
      console.log(error)
      enqueueSnackbar('Something went wrong!', { variant: 'error' })
    })
  }

  const rejectRequest = (invite) => {
    Service.patch('/api/project/user/invite/reject_user_request_or_invite/', {
      headers: {
        Authorization: 'Token ' + token,
      },
      data: {
       invite_id: invite,
      },
    }).then(() => {
      enqueueSnackbar('User rejected successfully!', { variant: 'success' })
      getData()
    }).catch((error) => {
      console.log(error)
      enqueueSnackbar('Something went wrong!', { variant: 'error' })
    })
  }

  const canApprove = Boolean(
    approveValues.start_date &&
    approveValues.end_date &&
    approveValues.project_access_list &&
    approveValues.access_group.length > 0 &&
    approveValues.access.length > 0
  )

  useEffect(() => {
    getData()
    Promise.all([getAccessList(), getAccessGroup()]).then(([accessListResponse, accessGroupResponse]) => {
      setAccessList(accessListResponse.data)
      setAccessGroup(accessGroupResponse.data.access.concat(accessGroupResponse.data.no_access))
    }).catch((error) => {
      console.log(error)
      enqueueSnackbar('Unable to load access parameters!', { variant: 'error' })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      {loading ? <Box display='flex' justifyContent='center' alignItems='center' width='100%'><CircularProgress /></Box> :
        <TableContainer component={Paper}>
          <Box width='100%' display='flex' justifyContent='space-between' alignItems='center' padding={2}>
            <Typography variant='h6' fontWeight='bold'>
              {props.title || 'User Requests'}
            </Typography>
          </Box>
          <Table size='small' aria-label='user requests'>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ?
                <TableRow>
                  <TableCell colSpan={5} align='center'>No user requests found.</TableCell>
                </TableRow> :
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{`${request?.first_name || ''} ${request?.last_name || ''}`.trim()}</TableCell>
                    <TableCell>{request.user_details?.email || request.email || ''}</TableCell>
                    <TableCell>{request.project_details?.name || request.project_name || props.project?.name || ''}</TableCell>
                    <TableCell>{request.status_name || (request.status === 0 ? 'Pending' : request.status === 1 ? 'Approved' : request.status === 2 ? 'Rejected' : '')}</TableCell>
                    <TableCell>
                      <Tooltip title='Approve'>
                        <IconButton onClick={() => openApproveDialog(request.id)}>
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Reject'>
                        <IconButton onClick={() => rejectRequest(request.id)}>
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>}
      <Dialog open={approveDialogOpen} onClose={resetApproveDialog} fullWidth maxWidth='xs'>
        <DialogTitle>Approve User Request</DialogTitle>
        <DialogContent sx={{ width: '100%' }}>
          <Box marginTop={1}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Start Date'
                format='DD/MM/YYYY'
                value={approveValues.start_date}
                onChange={(value) => setApproveValues({ ...approveValues, start_date: value })}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
          </Box>
          <Box marginTop={1}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='End Date'
                format='DD/MM/YYYY'
                minDate={approveValues.start_date}
                value={approveValues.end_date}
                onChange={(value) => setApproveValues({ ...approveValues, end_date: value })}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
          </Box>
          <Box marginTop={1}>
            <Autocomplete
              value={approveValues.project_access_list}
              onChange={(event, value) => setApproveValues({ ...approveValues, project_access_list: value })}
              options={accessList}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label='Access List' />}
            />
          </Box>
          <Box marginTop={1}>
            <Autocomplete
              multiple
              value={approveValues.access_group}
              onChange={(event, value) => {
                setApproveValues({ ...approveValues, access_group: value, access: [] })
                if (value.length > 0) {
                  getAccess(value[0].id).then((response) => setAccess(response.data)).catch((error) => console.log(error))
                } else {
                  setAccess([])
                }
              }}
              options={accessGroup}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label='Access Group' />}
            />
          </Box>
          <Box marginTop={1}>
            <Autocomplete
              multiple
              value={approveValues.access}
              onChange={(event, value) => setApproveValues({ ...approveValues, access: value })}
              options={access}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label='Access' />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetApproveDialog} size='small' variant='contained'>Cancel</Button>
          <Button onClick={approveRequest} disabled={!canApprove} size='small' variant='contained'>Approve</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default UserRequests